import React, { useState } from 'react';
import { X, AlertTriangle, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { templateService } from '../../services/templateService';
import toast from 'react-hot-toast';

const TemplateSubmissionModal = ({ isOpen, onClose, template, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const handleSubmit = async () => {
    if (!template) return;

    setIsSubmitting(true);
    setSubmissionResult(null);

    try {
      const result = await templateService.submitTemplateToWhatsApp(template.templateId);
      setSubmissionResult(result);
      toast.success('Template submitted to WhatsApp successfully');
      onSuccess();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to submit template to WhatsApp';
      setSubmissionResult({
        success: false,
        message: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSubmissionResult(null);
      onClose();
    }
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            Submit Template to WhatsApp
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Template Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Template Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{template.templateName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Category:</span>
                <span className="font-medium">{template.templateCategory}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Language:</span>
                <span className="font-medium">{template.language}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Current Status:</span>
                <span className={`font-medium ${
                  template.templateStatus === 'APPROVED' ? 'text-green-600' :
                  template.templateStatus === 'PENDING' ? 'text-yellow-600' :
                  template.templateStatus === 'REJECTED' ? 'text-red-600' :
                  'text-gray-600'
                }`}>
                  {template.templateStatus}
                </span>
              </div>
            </div>
          </div>

          {/* Content Preview */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Content Preview</h3>
            <div className="bg-white border rounded-lg p-3 text-sm text-gray-800">
              {template.content}
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <h4 className="font-medium mb-2">Important Notes:</h4>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Once submitted, the template will be reviewed by WhatsApp</li>
                  <li>Review process typically takes 24-48 hours</li>
                  <li>You cannot edit the template while it's under review</li>
                  <li>Make sure your template follows WhatsApp's policies</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submission Result */}
          {submissionResult && (
            <div className={`rounded-lg p-4 mb-6 ${
              submissionResult.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start">
                {submissionResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                )}
                <div className="text-sm">
                  <h4 className={`font-medium mb-1 ${
                    submissionResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {submissionResult.success ? 'Submission Successful' : 'Submission Failed'}
                  </h4>
                  <p className={submissionResult.success ? 'text-green-700' : 'text-red-700'}>
                    {submissionResult.message || submissionResult.id}
                  </p>
                  {submissionResult.success && submissionResult.id && (
                    <p className="text-green-700 mt-1">
                      Template ID: {submissionResult.id}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Actions Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="btn-secondary disabled:opacity-50"
          >
            {submissionResult ? 'Close' : 'Cancel'}
          </button>
          {!submissionResult && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn-primary disabled:opacity-50 flex items-center"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit to WhatsApp
            </button>
          )}
          {submissionResult?.success && (
            <a
              href="https://business.whatsapp.com/products/message-api"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View in WhatsApp Business
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateSubmissionModal;
